<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Brand logo-->
      <b-link class="brand-logo">
        <img
          src="/nirvana-memorial-garden-logo.png"
          alt="Logo"
        >
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col
        lg="8"
        class="d-none d-lg-flex align-items-center p-5"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="imgUrl"
            alt="Login V2"
          />
        </div>
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-title
            class="mb-1 font-weight-bold"
            title-tag="h2"
          >
            Welcome to Nirvana! 👋
          </b-card-title>
          <b-card-text
            v-if="newUser"
            class="mb-2"
          >
            Setup your account to continue.
          </b-card-text>
          <b-card-text
            v-else
            class="mb-2"
          >
            Please sign-in to your account and start the adventure
          </b-card-text>

          <!-- form -->
          <validation-observer
            ref="loginForm"
            #default="{invalid}"
          >
            <b-card-text
              v-if="errorMessage"
              class="mb-2 text-danger"
            >
              <strong>{{ errorMessage }}</strong>
            </b-card-text>
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="login"
            >
              <!-- email -->
              <b-form-group
                label="Username"
                label-for="login-email"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Username"
                  vid="email"
                  rules="required|email"
                >
                  <b-form-input
                    id="login-email"
                    v-model="userEmail"
                    :state="errors.length > 0 ? false:null"
                    name="login-email"
                    placeholder="john@example.com"
                    :disabled="newUser"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- forgot password -->
              <!-- forgot password -->
              <b-form-group v-if="newUser">
                <div class="d-flex justify-content-between">
                  <label for="login-password">Set a password</label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  vid="password"
                  rules="required|newPassword"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid':null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="password"
                      :state="errors.length > 0 ? false:null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="Password"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                  <br>
                  <small>• Password must be at least 8 characters long</small>
                  <br>
                  <small>• Include at least one special symbol (e.g. ! @ # $ % ^ & ).</small>
                </validation-provider>
              </b-form-group>
              <b-form-group v-else>
                <div class="d-flex justify-content-between">
                  <label for="login-password">Password</label>
                  <b-link :to="{name:'auth-forgot-password-v1'}">
                    <small>Forgot Password?</small>
                  </b-link>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Password"
                  vid="password"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid':null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="password"
                      :state="errors.length > 0 ? false:null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="Password"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- checkbox -->
              <b-form-group v-if="!newUser">
                <b-form-checkbox
                  id="remember-me"
                  v-model="remember"
                  name="checkbox-1"
                >
                  Remember Me
                </b-form-checkbox>
              </b-form-group>

              <!-- submit buttons -->
              <b-button
                v-if="newUser"
                type="submit"
                variant="primary"
                block
                :disabled="invalid"
              >
                Continue
              </b-button>
              <b-button
                v-else
                type="submit"
                variant="primary"
                block
                :disabled="invalid"
              >
                Sign in
              </b-button>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
    <!-- /Login-->
    </b-row>

    <b-modal
      id="two-fa-setup-modal"
      ref="two-fa-setup-modal"
      modal-class="status-update-modal"
      centered
      no-stacking
      no-close-on-backdrop
      header-class="front-modal block-header"
      body-class="modal-body-custom-padding"
      footer-class="center-button"
      @cancel="close2FASetupModal"
      @ok="open2FASetupOtpModal"
    >
      <template #modal-header>
        <div class="text-center">
          <p class="fs-16 weight-500 mb-0">
            Set up authenticator app:
          </p>
          <p class="mb-0">
            1. In the Google Authenticator app tap the <span class="weight-700">+</span>
          </p>
          <p class="mb-0">
            2. Choose <span class="weight-700">Scan a QR code</span>
          </p>
          <hr class="two-fa-modal-hr">
        </div>
      </template>
      <b-card-text class="text-center">
        <b-img
          class=""
          height="200"
          width="200"
          :src="qrUrl"
        />
        <p class="mb-0 fs-21 weight-600 mt-30">
          Authenticator QR code
        </p>
        <b-button
          variant="link"
          class="p-0 mt-0"
          @click="openHelpModal"
        >
          Can’t scan it?
        </b-button>
      </b-card-text>
      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="outline-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="custom-icon"
          @click="ok()"
        >
          <span class="align-middle">Next</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="two-fa-help-modal"
      ref="two-fa-help-modal"
      modal-class="status-update-modal"
      centered
      no-stacking
      no-close-on-backdrop
      header-class="front-modal block-header"
      body-class="modal-body-custom-padding"
      footer-class="center-button"
      size="563"
      @cancel="close2FAHelpModal"
      @ok="open2FASetupModal"
    >
      <template #modal-header>
        <div class="text-center">
          <p class="fs-21 weight-600 mb-50">
            Having trouble scanning the QR code?
          </p>
          <p class="fs-16 weight-500 mb-0">
            If you're unable to scan it, please email our admin<br>team at <span class="text-primary">{{ contactEmail }}</span> for assistance.
          </p>
          <p class="fs-16 weight-500 mt-2 mb-0">
            We’ll be happy to help you out.
          </p>
        </div>
      </template>
      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="outline-primary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="custom-icon"
          @click="ok()"
        >
          <span class="align-middle">Try Again</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="two-fa-setup-otp-modal"
      ref="two-fa-setup-otp-modal"
      modal-class="status-update-modal two-fa-otp"
      centered
      no-stacking
      no-close-on-backdrop
      header-class="front-modal block-header"
      body-class="modal-body-custom-padding"
      footer-class="full-width-button"
    >
      <template #modal-header>
        <div class="text-center">
          <p class="fs-16 weight-500 mb-0">
            Set up authenticator app:
          </p>
          <p class="mb-0">
            Enter the 6-digit code you see in your app
          </p>
        </div>
      </template>
      <div class="mt-30">
        <b-form @submit.prevent="submitSetupOtp">
          <validation-observer
            ref="twoFASetupOtpForm"
          >
            <b-form-group
              label="Enter code"
              label-for="setup-otp"
            >
              <validation-provider
                #default="{ errors }"
                name="Code"
                vid="code"
                rules="required"
              >
                <b-form-input
                  id="setup-otp"
                  v-model="token"
                  :state="errors.length > 0 ? false : null"
                  name="code"
                />
                <small
                  v-if="errors.length"
                  class="text-danger"
                >
                  {{ errors[0] }}
                </small>
              </validation-provider>
            </b-form-group>
          </validation-observer>
        </b-form>
      </div>
      <template #modal-footer>
        <b-button
          variant="flat-primary"
          class="mr-auto p-0"
          @click="open2FASetupModal"
        >
          Back
        </b-button>
        <b-button
          variant="outline-primary"
          @click="close2FASetupOtpModal"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="custom-icon"
          @click="submitSetupOtp"
        >
          <span class="align-middle">Verify</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="two-fa-otp-modal"
      ref="two-fa-otp-modal"
      modal-class="status-update-modal two-fa-otp"
      centered
      no-stacking
      no-close-on-backdrop
      header-class="front-modal block-header"
      body-class="modal-body-custom-padding"
      footer-class="full-width-button"
    >
      <template #modal-header>
        <div class="">
          <p class="fs-16 weight-500 mb-0">
            Enter the 6-digit code you see in your app
          </p>
        </div>
      </template>
      <div class="mt-30">
        <b-form @submit.prevent="submit2FAOtp">
          <validation-observer
            ref="twoFASetupOtpForm"
          >
            <b-form-group
              label="Enter code"
              label-for="setup-otp"
            >
              <validation-provider
                #default="{ errors }"
                name="Code"
                vid="code"
                rules="required"
              >
                <b-form-input
                  id="setup-otp"
                  v-model="token"
                  :state="errors.length > 0 ? false : null"
                  name="code"
                />
                <small
                  v-if="errors.length"
                  class="text-danger"
                >
                  {{ errors[0] }}
                </small>
              </validation-provider>
            </b-form-group>
          </validation-observer>
        </b-form>
      </div>
      <template #modal-footer>
        <b-button
          variant="flat-primary"
          class="mr-auto p-0"
          @click="close2FAOtpModal"
        >
          Back
        </b-button>
        <b-button
          variant="outline-primary"
          @click="close2FAOtpModal"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="custom-icon"
          @click="submit2FAOtp"
        >
          <span class="align-middle">Verify</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
// import VuexyLogo from '@core/layouts/components/Logo.vue'
import {
  BRow, BCol, BLink, BFormGroup, BFormCheckbox, BFormInput, BInputGroupAppend, BInputGroup, BCardText, BCardTitle, BImg, BForm, BButton, VBTooltip,
} from 'bootstrap-vue'
// import useJwt from '@/auth/jwt/useJwt'
import { required, email, newPassword } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store/index'
// import { getHomeRouteForLoggedInUser } from '@/auth/utils'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormCheckbox,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    // BFormCheckbox,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      remember: localStorage.getItem('remember_me') ?? false,
      password: '',
      userEmail: localStorage.getItem('userEmail') ?? '',
      sideImg: require('@/assets/images/pages/login-v2.svg'),
      errorMessage: null,
      token: '',
      qrUrl: '',
      userData: {},
      accessToken: '',
      userSecret: '',
      contactEmail: 'support@nirvana.com.sg',
      newUser: false,

      // validation rules
      required,
      email,
      newPassword,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/login-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  created() {
    if (this.$route.query.email && this.$route.query.setup === 'true') {
      this.$http.post('/check-user', { email: this.$route.query.email }).then(res => {
        if (res.data.exist && !res.data.password) {
          this.userEmail = this.$route.query.email
          this.newUser = true
        }
      })
    }
  },
  methods: {
    login() {
      this.$refs.loginForm.validate().then(success => {
        if (success) {
          // useJwt.login({
          //   email: this.userEmail,
          //   password: this.password,
          // })
          store
            .dispatch('auth/login', { email: this.userEmail, password: this.password, newUser: this.newUser })
            .then(response => {
              const {
                userData, accessToken, contactEmail, twoFA,
              } = response.data
              if (twoFA === 'true') {
                this.contactEmail = contactEmail
                this.userData = userData
                this.accessToken = accessToken
                if (userData.twoFA) {
                  this.$root.$emit('bv::show::modal', 'two-fa-otp-modal', '')
                } else {
                  this.qrUrl = response.data.qrCode
                  this.userSecret = response.data.secret
                  this.$root.$emit('bv::show::modal', 'two-fa-setup-modal', '')
                }
              } else {
                store.commit('auth/auth_success', { accessToken, userData })
                if (this.remember) {
                  localStorage.setItem('remember_me', this.remember)
                  localStorage.setItem('userEmail', this.userEmail)
                } else {
                  localStorage.setItem('remember_me', false)
                  localStorage.setItem('userEmail', '')
                }
                this.$ability.update(userData.permissions)

                this.$router.replace(userData.userRedirectURL || '/')
                  .then(() => {
                    this.$toast({
                      component: ToastificationContent,
                      position: 'top-right',
                      props: {
                        title: `Welcome ${userData.name || userData.email}`,
                        icon: 'CoffeeIcon',
                        variant: 'success',
                        text: `You have successfully logged in as ${userData.role}. Now you can start to explore!`,
                      },
                    })
                  })
                // useJwt.setToken(response.data.accessToken)
                // useJwt.setRefreshToken(response.data.refreshToken)
                // localStorage.setItem('userData', JSON.stringify(userData))
                // if (this.remember) {
                //   localStorage.setItem('remember_me', this.remember)
                //   localStorage.setItem('userEmail', this.userEmail)
                // } else {
                //   localStorage.setItem('remember_me', false)
                //   localStorage.setItem('userEmail', '')
                // }
                // this.$ability.update(userData.permissions)

                // ? This is just for demo purpose as well.
                // ? Because we are showing eCommerce app's cart items count in navbar
                // this.$store.commit('app-ecommerce/UPDATE_CART_ITEMS_COUNT', userData.extras.eCommerceCartItemsCount)

                // ? This is just for demo purpose. Don't think CASL is role based in this case, we used role in if condition just for ease
                // this.$router.replace(userData.userRedirectURL || '/')
                //   .then(() => {
                //     this.$toast({
                //       component: ToastificationContent,
                //       position: 'top-right',
                //       props: {
                //         title: `Welcome ${userData.name || userData.email}`,
                //         icon: 'CoffeeIcon',
                //         variant: 'success',
                //         text: `You have successfully logged in as ${userData.role}. Now you can start to explore!`,
                //       },
                //     })
                //   })
              }
            })
            .catch(error => {
              // this.$refs.loginForm.setErrors(error.data.message)
              this.errorMessage = error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE
            })
        }
      })
    },
    openHelpModal() {
      this.$root.$emit('bv::hide::modal', 'two-fa-setup-modal', '')
      this.$root.$emit('bv::show::modal', 'two-fa-help-modal', '')
    },
    close2FAHelpModal() {
      this.$root.$emit('bv::hide::modal', 'two-fa-help-modal', '')
    },
    close2FASetupModal() {
      this.$root.$emit('bv::hide::modal', 'two-fa-setup-modal', '')
    },
    close2FASetupOtpModal() {
      this.$root.$emit('bv::hide::modal', 'two-fa-setup-otp-modal', '')
    },
    open2FASetupOtpModal() {
      this.$root.$emit('bv::hide::modal', 'two-fa-setup-modal', '')
      this.$root.$emit('bv::show::modal', 'two-fa-setup-otp-modal', '')
    },
    submitSetupOtp() {
      const formData = new FormData()

      formData.append('user', this.userData._id)
      formData.append('secret', this.userSecret)
      formData.append('token', this.token)
      this.$http.post('two-fa/setup/verify', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(() => {
          this.userData.twoFA = true
          store.commit('auth/auth_success', { accessToken: this.accessToken, userData: this.userData })
          if (this.remember) {
            localStorage.setItem('remember_me', this.remember)
            localStorage.setItem('userEmail', this.userEmail)
          } else {
            localStorage.setItem('remember_me', false)
            localStorage.setItem('userEmail', '')
          }
          this.$ability.update(this.userData.permissions)

          this.$router.replace(this.userData.userRedirectURL || '/')
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: `Welcome ${this.userData.name || this.userData.email}`,
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: `You have successfully logged in as ${this.userData.role}. Now you can start to explore!`,
                },
              })
            })
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    open2FASetupModal() {
      this.$root.$emit('bv::hide::modal', 'two-fa-setup-otp-modal', '')
      this.$root.$emit('bv::hide::modal', 'two-fa-help-modal', '')
      this.$root.$emit('bv::show::modal', 'two-fa-setup-modal', '')
    },
    close2FAOtpModal() {
      this.$root.$emit('bv::hide::modal', 'two-fa-otp-modal', '')
    },
    submit2FAOtp() {
      const formData = new FormData()

      formData.append('user', this.userData._id)
      formData.append('token', this.token)
      this.$http.post('two-fa/verify', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(() => {
          store.commit('auth/auth_success', { accessToken: this.accessToken, userData: this.userData })
          if (this.remember) {
            localStorage.setItem('remember_me', this.remember)
            localStorage.setItem('userEmail', this.userEmail)
          } else {
            localStorage.setItem('remember_me', false)
            localStorage.setItem('userEmail', '')
          }
          this.$ability.update(this.userData.permissions)

          this.$router.replace(this.userData.userRedirectURL || '/')
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: `Welcome ${this.userData.name || this.userData.email}`,
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: `You have successfully logged in as ${this.userData.role}. Now you can start to explore!`,
                },
              })
            })
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
